import { React, useState, useEffect } from 'react';
import { Tooltip } from "@mui/material";
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import dayjs from 'dayjs';
import AsyncSelect from 'react-select/async';
import { Toaster, toast } from 'react-hot-toast';
import SubmittingModal from 'components/Modal';
import { Base_URL } from '../../actions/Base_URL';

export default function AssignCreateInventory() {
  const navigate = useNavigate();
  const location = useLocation();
  const { type, id } = location.state || {};
  const [loading, setLoading] = useState(false);
  const [updateFlag, setUpdateFlag] = useState(false);
  const [inventoryData, setInventoryData] = useState([]);
  const [userData, setUserData] = useState([]);
  const [labData, setLabData] = useState([]);
  const [selectedLabId, setSelectedLabId] = useState(null);
  const [initialInventoryName, setInitialInventoryName] = useState('');
  const [maxAvailable, setMaxAvailable] = useState(1);
  const [checkingAvailability, setCheckingAvailability] = useState(false);

  const statusArray = [
    { id: 0, status: 'Select Status' },
    { id: 1, status: 'Issued' },
    { id: 2, status: 'Received' },
  ];

  let [formData, setFormData] = useState({
    registration_id: 0,
    user_id: 0,
    inventory_id: 0,
    issued_date: '',
    receiving_date: '',
    status: '',
    lab_name: '',
    user_name: '',
    lab_id: '',
    quantity: 1
  });

  let [error, setError] = useState({
    registration_id: '',
    user_name: '',
    lab_name: '',
    user_id: '',
    inventory_id: '',
    issued_date: '',
    receiving_date: '',
    status: '',
    lab_id: '',
  });

  const formatDate = (date) => {
    return date ? dayjs(date).format('YYYY-MM-DD HH:mm:ss') : null;
  };

  useEffect(() => {
    // Fetch initial dropdown data on component mount
    getUserDropdown();
    getLabDropdown();
  }, []); // Empty dependency array means this runs once on mount

  useEffect(() => {
    // Fetch inventory data by ID if in edit/view mode
    if (id && (type === 'edit' || type === 'view')) {
      setUpdateFlag(true);
      getInventoryDataById(id);
    }
  }, [id, type]);

  useEffect(() => {
    // Fetch inventories for the selected lab
    if (selectedLabId) {
      getInventoryDropdownByLabId(selectedLabId);
    } else {
      setInventoryData([]);
      if (!(id && (type === 'edit' || type === 'view'))) {
        setFormData((prev) => ({ ...prev, inventory_id: 0 }));
      }
    }
  }, [selectedLabId, id, type]);

  function getFormValues(e) {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError({ ...error, [e.target.name]: '' });
  }

  useEffect(() => {
    if (formData.inventory_id && formData.issued_date && formData.receiving_date) {
      checkAvailability();
    }
  }, [formData.inventory_id, formData.issued_date, formData.receiving_date]);

  async function checkAvailability() {
    try {
      if (!formData.inventory_id || !formData.issued_date || !formData.receiving_date) return;

      setCheckingAvailability(true);
      const token = localStorage.getItem('token');
      // Format dates as YYYY-MM-DD for the API
      const issued = dayjs(formData.issued_date).format('YYYY-MM-DD HH:mm:ss');
      const receiving = dayjs(formData.receiving_date).format('YYYY-MM-DD HH:mm:ss');

      const response = await axios.get(`${Base_URL}/api/inventory/registration/checkAvailability`, {
        params: {
          inventory_id: formData.inventory_id,
          issued_date: issued,
          receiving_date: receiving
        },
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (response.data.success) {
        setMaxAvailable(response.data.available_quantity);
        // If current quantity is higher than available, reset to available (or 1)
        if (formData.quantity > response.data.available_quantity) {
          setFormData(prev => ({ ...prev, quantity: Math.max(1, response.data.available_quantity) }));
        }
      }

    } catch (error) {
      console.error("Error checking availability", error);
      // Optional: toast.error("Could not check availability");
    } finally {
      setCheckingAvailability(false);
    }
  }

  function validateForm() {
    let errors = {};
    let isValid = true;
    if (!formData.lab_name) {
      errors.lab_id = 'Lab is required';
      isValid = false;
    }
    if (!formData.user_id) {
      errors.user_id = 'User name is required';
      isValid = false;
    }
    if (!formData.inventory_id) {
      errors.inventory_id = 'Inventory name is required';
      isValid = false;
    }
    if (!formData.issued_date) {
      errors.issued_date = 'Issued date is required';
      isValid = false;
    }
    if (!formData.receiving_date) {
      errors.receiving_date = 'Received date is required';
      isValid = false;
    }
    if (!formData.status) {
      errors.status = 'Status is required';
      isValid = false;
    }
    if (!formData.quantity || formData.quantity < 1) {
      errors.quantity = 'Quantity must be at least 1';
      isValid = false;
    }
    setError(errors);
    return isValid;
  }

  async function getLabDropdown() {
    try {
      const token = localStorage.getItem('token');
      const getApiResponse = await axios.get(
        `${Base_URL}/labs/getAllLabs`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      if (getApiResponse.status === 200 && getApiResponse.data.success) {
        const labs = getApiResponse.data.data;
        setLabData(labs);
        // Auto-select if only 1 lab (Lab Incharge case)
        if (labs.length === 1) {
          const lab = labs[0];
          setSelectedLabId(lab.lab_id);
          setFormData(prev => ({ ...prev, lab_id: lab.lab_id, lab_name: lab.lab_name }));
        }
      } else {
        setLabData([]);
      }
    } catch (error) {
      toast.error('Error fetching labs: ' + (error.response?.data?.message || error.message));
      setLabData([]);
    }
  }

  async function getInventoryDropdownByLabId(labId) {
    try {
      const token = localStorage.getItem('token');
      const getApiResponse = await axios.get(
        `${Base_URL}/api/inventory/lab/${labId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      if (getApiResponse.status === 200) {
        const payload = getApiResponse.data;
        const normalizedList = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.inventories)
          ? payload.inventories
          : Array.isArray(payload?.data)
          ? payload.data
          : [];
        setInventoryData(normalizedList);
      } else {
        setInventoryData([]);
      }
    } catch (error) {
      toast.error(
        'Error fetching inventory for selected lab: ' + (error.response?.data?.message || error.message)
      );
      setInventoryData([]);
    }
  }

  async function getUserDropdown() {
    try {
      const token = localStorage.getItem('token');
      const getApiResponse = await axios.get(
        `${Base_URL}/user/getAllUsers`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      if (getApiResponse.status === 200 && getApiResponse.data.success) {
        setUserData(getApiResponse.data.data);
      } else {
        setUserData([]);
      }
    } catch (error) {
      toast.error('Error fetching users: ' + (error.response?.data?.message || error.message));
      setUserData([]);
    }
  }

  const loadInventoryOptions = (inputValue, callback) => {
    // If inventoryData is empty, we might be in edit/view mode and fetching it
    // Or in create mode and no lab is selected yet
    if (!inventoryData.length && selectedLabId && (type === 'edit' || type === 'view') && initialInventoryName) {
      const initialOption = { value: formData.inventory_id, label: initialInventoryName };
      callback(inputValue ? [initialOption].filter(option => option.label.toLowerCase().includes(inputValue.toLowerCase())) : [initialOption]);
      return;
    }

    const filteredOptions = (Array.isArray(inventoryData) ? inventoryData : [])
      .filter((item) =>
        item.name.toLowerCase().includes(inputValue.toLowerCase())
      )
      .map((item) => ({
        value: item.id,
        label: item.name,
      }));
    callback(filteredOptions);
  };

  const loadUserOptions = async (inputValue, callback) => {
    try {
      // No need to call getUserDropdown again if userData is already populated
      if (!userData.length) {
        await getUserDropdown();
      }
      const filteredOptions = (Array.isArray(userData) ? userData : [])
        .filter((user) =>
          `${user.first_name} ${user.last_name || ""} ${user.email} ${user.admission_no || ""}`.toLowerCase().includes(inputValue.toLowerCase())
        )
        .map((user) => ({
          value: user.user_id,
          label: `${user.first_name} ${user.last_name || ""} | ${user.email} | ${user.admission_no || "N/A"}`,
        }));

      callback(filteredOptions);
    } catch (error) {
      toast.error('Error loading user options:', error);
      callback([]);
    }
  };

  async function getInventoryDataById(recordId) {
    try {
      const token = localStorage.getItem('token');
      let response = await axios.get(
        `${Base_URL}/api/inventory/registration/getInventoryRegistrationById/` +
        recordId,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      const registeredData = response.data.data;
      const registeredLabId = registeredData.lab_id;
      const registeredInventoryId = registeredData.inventory_id;

      // Set selectedLabId first, which will trigger fetching inventories for that lab
      setSelectedLabId(registeredLabId);
      //console.log("da",response, registeredLabId);

      // Fetch the name of the inventory if the inventory_id is available
      if (registeredInventoryId) {
        try {
          const inventoryResponse = await axios.get(
            `${Base_URL}/api/inventory/getInventoryById/${registeredInventoryId}`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json',
              },
            }
          );
          if (inventoryResponse.status === 200 && inventoryResponse.data) {
            setInitialInventoryName(inventoryResponse.data.name);
          }
        } catch (inventoryError) {
          console.error("Error fetching inventory name:", inventoryError);
          toast.error("Could not fetch inventory name for display.");
        }
      }

      setFormData({
        registration_id: registeredData.registration_id || 0,
        user_id: registeredData.user_id || 0,
        inventory_id: registeredData.inventory_id || 0,
        issued_date: registeredData.issued_date
          ? dayjs(registeredData.issued_date).format('YYYY-MM-DD')
          : '',
        receiving_date: registeredData.receiving_date
          ? dayjs(registeredData.receiving_date).format('YYYY-MM-DD')
          : '',
        status: registeredData.status,
        user_name: registeredData.user_name,
        lab_name: registeredData.lab_name,
        lab_id: registeredLabId,
        quantity: registeredData.quantity || 1
      });
    } catch (error) {
      toast.error(error.response?.data?.message || 'Something went wrong');
    }
  }

  async function onSubmitFormData(e) {
    e.preventDefault();
    if (type !== 'view' && !validateForm()) return;
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const apiUrl = !updateFlag
        ? `${Base_URL}/api/inventory/registration/createInventoryRegistration`
        : `${Base_URL}/api/inventory/registration/updateInventoryRegistration/` +
        id;
      const method = updateFlag ? 'put' : 'post';
      const response = await axios({
        method: !updateFlag ? 'post' : 'put',
        url: apiUrl,
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        data: formData,
      });

      if (response.status === 200 || response.status === 201) {
        toast.success(
          !updateFlag
            ? 'Inventory Assigned Successfully'
            : 'Inventory Assignment Updated Successfully'
        );
        const basePath = window.location.pathname.startsWith("/labincharge") ? "/labincharge" : "/admin";
        navigate(`${basePath}/assign-inventory`);
      }
    } catch (error) {
      toast.error(
        error.response?.data?.message ||
        (error.response?.data?.error ? error.response.data.error : 'Something went wrong')
      );
    } finally {
      setLoading(false);
    }
  }

  function clearFormData() {
    setUpdateFlag(false);
    setFormData({
      user_id: 0,
      inventory_id: 0,
      issued_date: '',
      receiving_date: '',
      status: '',
      quantity: 1,
    });
    setSelectedLabId(null); // Clear selected lab
    setInitialInventoryName(''); // Clear initial inventory name
    setError({
      registration_id: '',
      user_id: '',
      inventory_id: '',
      issued_date: '',
      receiving_date: '',
      status: '',
      lab_id: '',
    });
  }

  // --- UI Section ---
  return (
    <>
      <Toaster position="bottom-left" reverseOrder={false} />
      <SubmittingModal loading={loading} />
      <div className="flex justify-center items-center">
        <div className="relative flex flex-col min-w-0 break-words w-full mb-6 mt-6 shadow-lg rounded-lg bg-blueGray-100 border-0">
          <div className="rounded-t bg-white mb-0 px-6 py-6">
            <div className="text-center flex justify-between">
              <h6 className="text-blueGray-700 text-xl font-semibold">
                {type === 'create'
                  ? 'Inventory Booking'
                  : type === 'edit'
                    ? 'Edit Inventory'
                    : 'View Inventory'}
              </h6>
            </div>
          </div>
          <div className="flex-auto px-4 lg:px-10 py-10 pt-0">
            <form onSubmit={onSubmitFormData}>
              <h6 className="text-blueGray-700 text-xl mt-3 mb-6 font-semibold ">
                {type === 'create'
                  ? 'Inventory Information'
                  : type === 'edit'
                    ? 'Edit Inventory Information'
                    : 'View Inventory Details'}
              </h6>
              <div className="flex flex-wrap">
                {/* Select Student */}
                <div className="w-full lg:w-6/12 px-4">
                  <div className="relative w-full mb-3">
                    <label
                      className="block text-blueGray-600 text-xs font-bold mb-2"
                      htmlFor="grid-password"
                    >
                      Select Student <span className="text-red-500">*</span>
                    </label>
                    <AsyncSelect
                      isDisabled={type === 'view'}
                      cacheOptions
                      loadOptions={loadUserOptions}
                      defaultOptions={(Array.isArray(userData) ? userData : []).map((user) => ({
                        value: user?.user_id || 0,
                        label: `${user?.first_name || ""} ${user?.last_name || ""} | ${user?.email || ""} | ${user?.admission_no || "N/A"}`,
                      }))}
                      value={
                        (Array.isArray(userData) ? userData : []).find((user) => user?.user_id === formData.user_id)
                          ? {
                            value: formData.user_id,
                            label: (() => {
                              const u = (Array.isArray(userData) ? userData : []).find(user => user?.user_id === formData.user_id);
                              return `${u?.first_name || ""} ${u?.last_name || ""} | ${u?.email || ""} | ${u?.admission_no || "N/A"}`;
                            })(),
                          }
                          : {
                            value: formData.user_id,
                            label: formData.user_name
                          }
                      }
                      onChange={(selectedOption) => {
                        setFormData({
                          ...formData,
                          user_id: selectedOption ? selectedOption.value : 0,
                        });
                        setError({ ...error, user_id: '' });
                      }}
                      classNamePrefix="react-select"
                      placeholder="Search Student"
                      noOptionsMessage={() => 'No Data Found'}
                      styles={{
                        control: (provided, state) => ({
                          ...provided,
                          border: '1px solid #D1D5DB',
                          padding: '0px',
                          height: '42px',
                          minHeight: '42px',
                          borderRadius: '0.375rem',
                          boxShadow: state.isFocused ? '0 0 0 2px #3B82F6' : 'none',
                          backgroundColor: state.isDisabled ? '#F3F4F6' : 'white',
                          pointerEvents: state.isDisabled ? 'none' : 'auto',
                        }),
                        valueContainer: (provided) => ({
                          ...provided,
                          padding: '6px 12px',
                        }),
                        indicatorsContainer: (provided) => ({
                          ...provided,
                          height: '42px',
                        }),
                        input: (provided) => ({
                          ...provided,
                          margin: '0',
                          padding: '0',
                          border: 'none',
                          boxShadow: 'none',
                          '& input': {
                            border: 'none !important',
                            boxShadow: 'none !important',
                          },
                        }),
                        option: (provided, state) => ({
                          ...provided,
                          backgroundColor: state.isSelected ? '#3B82F6' : state.isFocused ? '#F3F4F6' : 'white',
                          color: state.isSelected ? 'white' : '#111827',
                          cursor: 'pointer',
                          '&:active': {
                            backgroundColor: '#3B82F6',
                          },
                        }),
                      }}
                    />
                    {error.user_id && (
                      <p className="text-red-500 text-xs mt-1">{error.user_id}</p>
                    )}
                  </div>
                </div>

                {/* Select Lab */}
                <div className="w-full lg:w-6/12 px-4">
                  <div className="relative w-full mb-3">
                    <label
                      className="block text-blueGray-600 text-xs font-bold mb-2"
                      htmlFor="select-lab"
                    >
                      Select Lab
                    </label>
                    <AsyncSelect
                      isDisabled={type === 'view'}
                      cacheOptions
                      loadOptions={async (inputValue, callback) => {
                        // Ensure labData is loaded before filtering
                        if (!labData.length) {
                          await getLabDropdown(); // Re-fetch if not already fetched
                        }
                        const filteredOptions = (Array.isArray(labData) ? labData : [])
                          .filter((lab) =>
                            lab.lab_name.toLowerCase().includes(inputValue.toLowerCase())
                          )
                          .map((lab) => ({
                            value: lab.lab_id,
                            label: lab.lab_name,
                          }));
                        callback(filteredOptions);
                      }}
                      defaultOptions={(Array.isArray(labData) ? labData : []).map((lab) => ({
                        value: lab?.lab_id || 0,
                        label: lab?.lab_name || '',
                      }))}
                      value={
                        selectedLabId
                          ? {
                            value: selectedLabId,
                            label:
                              (Array.isArray(labData) ? labData : []).find((lab) => lab?.lab_id === selectedLabId)
                                ?.lab_name || '',
                          }
                          : {
                            value: formData.lab_id,
                            label: formData.lab_name
                          }
                      }
                      onChange={(selectedOption, e) => {
                        setSelectedLabId(selectedOption ? selectedOption.value : null);
                        setFormData((prev) => ({ ...prev, lab_id: selectedOption.value, lab_name: selectedOption.label, inventory_id: 0 })); // Reset inventory when lab changes
                        setInitialInventoryName(''); // Clear initial inventory name if lab changes
                        setError({ ...error, lab_id: '', lab_name: '' });
                      }}
                      classNamePrefix="react-select"
                      placeholder="Search Lab"
                      noOptionsMessage={() => 'No Data Found'}
                      styles={{
                        control: (provided, state) => ({
                          ...provided,
                          border: '1px solid #D1D5DB',
                          padding: '0px',
                          height: '42px',
                          minHeight: '42px',
                          borderRadius: '0.375rem',
                          boxShadow: state.isFocused ? '0 0 0 2px #3B82F6' : 'none',
                          backgroundColor: state.isDisabled ? '#F3F4F6' : 'white',
                          pointerEvents: state.isDisabled ? 'none' : 'auto',
                        }),
                        valueContainer: (provided) => ({
                          ...provided,
                          padding: '6px 12px',
                        }),
                        indicatorsContainer: (provided) => ({
                          ...provided,
                          height: '42px',
                        }),
                        input: (provided) => ({
                          ...provided,
                          margin: '0',
                          padding: '0',
                          border: 'none',
                          boxShadow: 'none',
                          '& input': {
                            border: 'none !important',
                            boxShadow: 'none !important',
                          },
                        }),
                        option: (provided, state) => ({
                          ...provided,
                          backgroundColor: state.isSelected ? '#3B82F6' : state.isFocused ? '#F3F4F6' : 'white',
                          color: state.isSelected ? 'white' : '#111827',
                          cursor: 'pointer',
                          '&:active': {
                            backgroundColor: '#3B82F6',
                          },
                        }),
                      }}
                    />
                    {error.lab_id && (
                      <p className="text-red-500 text-xs mt-1">{error.lab_id}</p>
                    )}
                  </div>
                </div>

                <div className="w-full lg:w-6/12 px-4">
                  <div className="relative w-full mb-3">
                    <label
                      className="block text-blueGray-600 text-xs font-bold mb-2"
                      htmlFor="grid-password"
                    >
                      Select Inventory <span className="text-red-500">*</span>
                    </label>
                    <AsyncSelect
                      isDisabled={type === 'view' || !selectedLabId}
                      cacheOptions
                      loadOptions={loadInventoryOptions}
                      defaultOptions={(Array.isArray(inventoryData) ? inventoryData : []).map((item) => ({
                        value: item?.id || 0,
                        label: item?.name || '',
                      }))}
                      value={
                        formData.inventory_id && ((Array.isArray(inventoryData) ? inventoryData : []).find((item) => item.id === formData.inventory_id) || initialInventoryName)
                          ? {
                            value: formData.inventory_id,
                            label: (Array.isArray(inventoryData) ? inventoryData : []).find(
                              (item) => item.id === formData.inventory_id
                            )?.name || initialInventoryName, // Use initialInventoryName if not found in current inventoryData
                          }
                          : null
                      }
                      onChange={(selectedOption) => {
                        setFormData({
                          ...formData,
                          inventory_id: selectedOption ? selectedOption.value : 0,
                        });
                        setError({ ...error, inventory_id: '' });
                      }}
                      classNamePrefix="react-select"
                      placeholder={selectedLabId ? "Search Inventory" : "Select a lab first"}
                      noOptionsMessage={() => (selectedLabId ? 'No Data Found' : 'Select a lab to see inventories')}
                      styles={{
                        control: (provided, state) => ({
                          ...provided,
                          border: '1px solid #D1D5DB',
                          padding: '0px',
                          height: '42px',
                          minHeight: '42px',
                          borderRadius: '0.375rem',
                          boxShadow: state.isFocused ? '0 0 0 2px #3B82F6' : 'none',
                          backgroundColor: state.isDisabled ? '#F3F4F6' : 'white',
                          pointerEvents: state.isDisabled ? 'none' : 'auto',
                        }),
                        valueContainer: (provided) => ({
                          ...provided,
                          padding: '6px 12px',
                        }),
                        indicatorsContainer: (provided) => ({
                          ...provided,
                          height: '42px',
                        }),
                        input: (provided) => ({
                          ...provided,
                          margin: '0',
                          padding: '0',
                          border: 'none',
                          boxShadow: 'none',
                          '& input': {
                            border: 'none !important',
                            boxShadow: 'none !important',
                          },
                        }),
                        option: (provided, state) => ({
                          ...provided,
                          backgroundColor: state.isSelected ? '#3B82F6' : state.isFocused ? '#F3F4F6' : 'white',
                          color: state.isSelected ? 'white' : '#111827',
                          cursor: 'pointer',
                          '&:active': {
                            backgroundColor: '#3B82F6',
                          },
                        }),
                      }}
                    />
                    {error.inventory_id && (
                      <p className="text-red-500 text-xs mt-1">
                        {error.inventory_id}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap">
                <div className="w-full lg:w-6/12 px-4">
                  <div className="relative w-full mb-3">
                    <label
                      className="block text-blueGray-600 text-xs font-bold mb-2"
                      htmlFor="grid-password"
                    >
                      Issued Date <span className="text-red-500">*</span>
                    </label>
                    <input
                      disabled={type === 'view'}
                      className="border-0 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
                      type="date"
                      name="issued_date"
                      value={formData.issued_date}
                      onChange={getFormValues}
                    />
                    {error.issued_date && (
                      <p className="text-red-500 text-xs mt-1">
                        {error.issued_date}
                      </p>
                    )}
                  </div>
                </div>
                <div className="w-full lg:w-6/12 px-4">
                  <div className="relative w-full mb-3">
                    <label
                      className="block text-blueGray-600 text-xs font-bold mb-2"
                      htmlFor="grid-password"
                    >
                      Return Date <span className="text-red-500">*</span>
                    </label>
                    <input
                      disabled={type === 'view'}
                      className="border-0 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
                      type="date"
                      name="receiving_date"
                      value={formData.receiving_date}
                      onChange={getFormValues}
                    />
                    {error.receiving_date && (
                      <p className="text-red-500 text-xs mt-1">
                        {error.receiving_date}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap">
                {/* Quantity Input */}
                <div className="w-full lg:w-6/12 px-4">
                  <div className="relative w-full mb-3">
                    <label
                      className="block text-blueGray-600 text-xs font-bold mb-2"
                      htmlFor="grid-password"
                    >
                      Quantity {checkingAvailability && "(Checking...)"} {maxAvailable !== null && !checkingAvailability && `(Max: ${maxAvailable})`} <span className="text-red-500">*</span>
                    </label>
                    <select
                      className="border-0 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
                      value={formData.quantity}
                      onChange={(e) => getFormValues(e)}
                      name="quantity"
                      disabled={type === 'view' || checkingAvailability}
                    >
                      {Array.from({ length: Math.max(0, maxAvailable || 0) }, (_, i) => i + 1).map(num => (
                        <option key={num} value={num}>
                          {num}
                        </option>
                      ))}
                    </select>
                    {error.quantity && (
                      <p className="text-red-500 text-xs mt-1">{error.quantity}</p>
                    )}
                  </div>
                </div>

                <div className="w-full lg:w-6/12 px-4">
                  <label
                    className="block text-blueGray-600 text-xs font-bold mb-2"
                    htmlFor="grid-password"
                  >
                    Status <span className="text-red-500">*</span>
                  </label>
                  <select
                    disabled={type === 'view'}
                    name="status"
                    value={formData.status}
                    onChange={getFormValues}
                    className="border-0 px-3 py-3 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
                  >
                    {statusArray.map((x) => (
                      <option key={x.id} value={x.id}>
                        {x.status}
                      </option>
                    ))}
                  </select>
                  {error.status && (
                    <p className="text-red-500 text-xs mt-1">{error.status}</p>
                  )}
                </div>
              </div>
              <div className="flex justify-end w-full">
                <Tooltip title="Back" arrow>
                  <button
                    className="mt-10 text-white bg-blueGray-700 active:bg-blueGray-600 font-semibold text-sm px-4 py-2 rounded shadow hover:shadow-md outline-none focus:outline-none mr-auto ease-linear transition-all duration-150"
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      const basePath = window.location.pathname.startsWith("/labincharge") ? "/labincharge" : "/admin";
                      navigate(`${basePath}/assign-inventory`);
                    }}
                  >
                    {'<< '}Back
                  </button>
                </Tooltip>
                {type !== 'view' ? (
                  <>
                    <Tooltip title="Submit" arrow>
                      <button
                        className="bg-lightBlue-500 mt-10 text-white active:bg-lightBlue-600 font-semibold text-sm px-4 py-2 rounded shadow hover:shadow-md outline-none focus:outline-none mr-1 ease-linear transition-all duration-150"
                        type="submit"
                      >
                        {!updateFlag ? 'Submit' : 'Update'}
                      </button>
                    </Tooltip>
                    <Tooltip title="Clear" arrow>
                      <button
                        className="bg-red-500 mt-10 text-white active:bg-lightBlue-600 font-semibold text-sm px-4 py-2 rounded shadow hover:shadow-md outline-none focus:outline-none mr-1 ease-linear transition-all duration-150"
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          clearFormData();
                        }}
                      >
                        Clear
                      </button>
                    </Tooltip>
                  </>
                ) : (
                  ''
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
}
